import React from 'react';
import {
  CircularProgress,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CmailComposer from '../../../cmail/components/CmailComposer';
import {
  useGetCmailMessage,
  useGetCmailThread,
} from '../../../cmail/CmailInbox/useGetCmailMessage';
import { generateReply } from '../../../../api/submissions/submissions.api';
import { useGetSubmissionById } from '../../../../components/hooks/queries/submissions/useGetSubmission';
import { processQuoteAttachments } from '../../../cmail/utils/quoteAttachmentUtils';
import { saveDraft, uploadAttachmentFromUrl } from '../../../../api/cmail.api';
import CbButton from '../../../../components/Buttons/CbButton';
import {
  extractRecipientsFromReceivers,
  extractEmailAddresses,
  filterOutCurrentUser,
  removeDuplicateEmails,
  formatEmailList,
} from '../../../cmail/utils/emailAddressUtils';
import type {
  MessageInfo,
  MessageInfoAttachment,
  MessageInfoReceiver,
} from '../../../../api/cmail/cmail.dto';
import { EmailRecipientType } from '../../../../api/cmail/cmail.dto';

interface CmailComposerModalProps {
  data: {
    msgId: string;
    submissionId: string;
  };
  close: () => void;
}

const CmailComposerModalComponent = ({
  data,
  close,
}: CmailComposerModalProps) => {
  const { msgId, submissionId } = data;

  // Get current user from Redux store
  const user = useSelector(
    (state: {
      user: { firstName?: string; lastName?: string; email?: string };
    }) => state.user
  );
  const userEmail = user?.email || '';

  // Create a stable timestamp for this modal instance to prevent caching
  const modalInstanceId = React.useMemo(() => Date.now(), []);

  // Reset processing flag when modal opens with new data
  React.useEffect(() => {
    hasProcessedAttachments.current = false;
  }, [msgId, submissionId]);

  // Fetch message info
  const messageQuery = useGetCmailMessage(msgId);
  const messageInfo = messageQuery.data;

  // Fetch submission data to get TSM and UW emails
  const submissionQuery = useGetSubmissionById(submissionId);
  const submission = submissionQuery.data;

  // Fetch thread data to get references
  const threadQuery = useGetCmailThread(msgId, 3, 20);

  // Get the current message with references from thread data
  const messageInfoWithReferences = React.useMemo(() => {
    if (!messageInfo) return messageInfo;

    // Try to get references from thread data first
    const threadMessage = threadQuery.data?.messages?.find(
      (m) => m.msgId === msgId
    );
    if (threadMessage?.references && threadMessage.references.length > 0) {
      return {
        ...messageInfo,
        references: threadMessage.references,
      };
    }

    // If no references exist, create a new references array with the original message's msgId
    // This is the correct behavior for email threading - the first reply should reference the original message
    return {
      ...messageInfo,
      references: [messageInfo.msgId],
    };
  }, [messageInfo, threadQuery.data, msgId]);

  // Parse sender name into first and last name
  const senderFirstName = React.useMemo(() => {
    if (messageInfoWithReferences?.senderFirstName)
      return messageInfoWithReferences.senderFirstName;
    const nameParts = messageInfoWithReferences?.senderName?.split(' ') || [];
    return nameParts[0] || '';
  }, [
    messageInfoWithReferences?.senderFirstName,
    messageInfoWithReferences?.senderName,
  ]);

  const senderLastName = React.useMemo(() => {
    if (messageInfoWithReferences?.senderLastName)
      return messageInfoWithReferences.senderLastName;
    const nameParts = messageInfoWithReferences?.senderName?.split(' ') || [];
    return nameParts.slice(1).join(' ') || '';
  }, [
    messageInfoWithReferences?.senderLastName,
    messageInfoWithReferences?.senderName,
  ]);

  // Extract recipient information for reply
  const replyRecipients = React.useMemo(() => {
    if (!messageInfoWithReferences) return { to: '', cc: '' };

    let originalTo: string[] = [];
    let originalCc: string[] = [];

    try {
      if (
        messageInfoWithReferences.receivers &&
        messageInfoWithReferences.receivers.length > 0
      ) {
        const recipients = extractRecipientsFromReceivers(
          messageInfoWithReferences.receivers
        );
        originalTo = recipients.to;
        originalCc = recipients.cc;
      } else {
        // Fall back to headers format
        originalTo = extractEmailAddresses(
          messageInfoWithReferences.headers?.To ||
            messageInfoWithReferences.headers?.to
        );
        originalCc = extractEmailAddresses(
          messageInfoWithReferences.headers?.Cc ||
            messageInfoWithReferences.headers?.cc ||
            messageInfoWithReferences.headers?.CC
        );
      }
    } catch (error) {
      console.warn('Failed to extract recipients from message:', error);
      originalTo = [];
      originalCc = [];
    }

    // The sender goes to "To" field
    const replyTo = messageInfoWithReferences.senderEmail || '';

    // All other recipients (excluding current user) go to "Cc" field
    const allOtherRecipients = removeDuplicateEmails(
      filterOutCurrentUser([...originalTo, ...originalCc], userEmail).filter(
        (email) =>
          email !== messageInfoWithReferences.senderEmail?.toLowerCase()
      )
    );

    // Add TSM and UW emails from submission data to CC (matching Release Quote behavior)
    const additionalCcEmails: string[] = [];
    const senderEmailLower = messageInfoWithReferences.senderEmail?.toLowerCase();
    
    if (submission?.tamEmail) {
      const tamEmailLower = submission.tamEmail.toLowerCase().trim();
      // Only add if it's a valid email and not the sender
      if (tamEmailLower && tamEmailLower !== senderEmailLower) {
        additionalCcEmails.push(tamEmailLower);
      }
    }
    if (submission?.uwEmail) {
      const uwEmailLower = submission.uwEmail.toLowerCase().trim();
      // Only add if it's a valid email and not the sender
      if (uwEmailLower && uwEmailLower !== senderEmailLower) {
        additionalCcEmails.push(uwEmailLower);
      }
    }
    if (submission?.correspondenceEmail) {
      const corrEmailLower = submission.correspondenceEmail.toLowerCase().trim();
      if (corrEmailLower && corrEmailLower !== senderEmailLower) {
        additionalCcEmails.push(corrEmailLower);
      }
    }

    // Combine original recipients with TSM/UW emails, removing duplicates and excluding current user
    const allCcRecipients = removeDuplicateEmails(
      filterOutCurrentUser(
        [...allOtherRecipients, ...additionalCcEmails],
        userEmail
      ).filter((email) => email && email.trim() !== '')
    );

    const replyCc = formatEmailList(allCcRecipients);

    return { to: replyTo, cc: replyCc };
  }, [messageInfoWithReferences, userEmail, submission?.tamEmail, submission?.uwEmail, submission?.correspondenceEmail]);

  // Get sender name for current user (similar to CmailComposer logic)
  const getCurrentUserSenderName = React.useCallback(() => {
    return `${user?.firstName} ${user?.lastName}`.trim();
  }, [user?.firstName, user?.lastName]);

  // Fetch AI-generated reply content using proper authenticated API
  // Use modalInstanceId to ensure fresh data each time modal opens
  // Wait for messageInfo to be available before calling generateReply
  const generateReplyQuery = useQuery({
    queryKey: [
      'generate-reply',
      submissionId,
      modalInstanceId,
      senderFirstName,
      senderLastName,
    ],
    queryFn: () =>
      generateReply(submissionId, {
        data: {
          htmlBodyAll:
            messageInfo?.textBodyMessage || messageInfo?.textBodyAll || '',
          senderFirstName,
          senderLastName,
          responseRole: 'CONCIERGE',
          temperature: 0.8,
          maxTokens: 2000,
          includeDetailedBreakdown: true,
        },
      }),
    enabled: Boolean(submissionId && messageInfo),
    staleTime: 0, // Consider data stale immediately
    cacheTime: 0, // Don't cache the data
  });
  const generatedReply = generateReplyQuery.data?.data;

  // Progressive loading messages for reply generation
  const [currentLoadingMessageIndex, setCurrentLoadingMessageIndex] =
    React.useState(0);

  const loadingMessages = React.useMemo(
    () => [
      'Reading submission email...',
      'Analyzing submission details...',
      'Reviewing submission status...',
      'Gathering relevant information...',
      'Generating personalized reply...',
    ],
    []
  );

  // Cycle through loading messages while generating reply
  React.useEffect(() => {
    if (!generateReplyQuery.isLoading) {
      setCurrentLoadingMessageIndex(0);
      return;
    }

    const intervalId = setInterval(() => {
      setCurrentLoadingMessageIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;
        return nextIndex >= loadingMessages.length ? 0 : nextIndex;
      });
    }, 2500); // Change message every 2.5 seconds

    return () => clearInterval(intervalId);
  }, [generateReplyQuery.isLoading, loadingMessages.length]);

  // Process quote attachments from coverageOutcomes
  const [quoteAttachments, setQuoteAttachments] = React.useState<
    MessageInfoAttachment[]
  >([]);
  const [isProcessingAttachments, setIsProcessingAttachments] =
    React.useState(false);
  const [draftMsgId, setDraftMsgId] = React.useState<string | null>(null);

  // Track if we've already processed attachments to prevent duplicates
  const hasProcessedAttachments = React.useRef(false);

  // Extract the async function using useCallback to prevent unnecessary re-creation
  const saveDraftAndUploadAttachments = React.useCallback(async () => {
    if (!generatedReply?.success || !generatedReply.coverageOutcomes) {
      return;
    }

    // Prevent duplicate processing
    if (hasProcessedAttachments.current) {
      return;
    }

    hasProcessedAttachments.current = true;
    setIsProcessingAttachments(true);

    try {
      // Create initial draft
      const draftResponse = await saveDraft({
        data: {
          to: replyRecipients.to,
          cc: replyRecipients.cc,
          subject: generatedReply.subject,
          body: generatedReply.bodyText,
          attachments: [],
          from: userEmail,
          senderName: getCurrentUserSenderName(),
          emailType: 'REPLY',
          replyToMsgId: messageInfoWithReferences?.msgId,
          references:
            messageInfoWithReferences?.references &&
            Array.isArray(messageInfoWithReferences.references)
              ? messageInfoWithReferences.references
              : undefined,
        },
      });

      const newDraftMsgId = draftResponse.data.msgId;
      setDraftMsgId(newDraftMsgId);

      // Process quote attachments and upload them
      const quoteAttachmentMetadata = await processQuoteAttachments(
        generatedReply
      );

      // Upload each quote attachment using the attachment-from-url API
      const uploadedAttachments = await Promise.all(
        quoteAttachmentMetadata.map(async (attachment) => {
          if (attachment.s3Path) {
            try {
              const uploadResponse = await uploadAttachmentFromUrl(
                newDraftMsgId,
                {
                  data: {
                    signedS3Url: attachment.s3Path,
                    docType: 'Quote',
                    fileName: attachment.docName,
                  },
                }
              );

              return uploadResponse.data;
            } catch (error) {
              console.error('Failed to upload attachment:', error);
              // Return a proper attachment structure even if upload fails
              return {
                ...attachment,
                s3Path: '', // Clear S3 path so it's not treated as a downloadable attachment
                docType: 'quote-proposal', // Ensure it's marked as a quote proposal
              };
            }
          }
          return attachment;
        })
      );

      setQuoteAttachments(uploadedAttachments);
    } catch (error) {
      console.error('Failed to process quote attachments:', error);
    } finally {
      setIsProcessingAttachments(false);
    }
  }, [
    generatedReply,
    messageInfoWithReferences,
    replyRecipients,
    userEmail,
    getCurrentUserSenderName,
  ]);

  React.useEffect(() => {
    if (generatedReply?.success && generatedReply.coverageOutcomes) {
      saveDraftAndUploadAttachments();
    }
  }, [
    generatedReply?.success,
    generatedReply?.coverageOutcomes,
    saveDraftAndUploadAttachments,
  ]);

  const handleClose = () => {
    close();
  };

  // Create a modified message object with AI-generated content and quote attachments
  const messageForComposer = React.useMemo(() => {
    if (!messageInfoWithReferences) {
      return messageInfoWithReferences;
    }

    // Build receivers array with TSM/UW emails added to CC
    const receiversWithTSMAndUW: MessageInfoReceiver[] = [];
    
    // Add original receivers
    if (messageInfoWithReferences.receivers && messageInfoWithReferences.receivers.length > 0) {
      receiversWithTSMAndUW.push(...messageInfoWithReferences.receivers);
    }
    
    // Add TSM and UW emails to CC if they exist and aren't already in receivers
    const senderEmailLower = messageInfoWithReferences.senderEmail?.toLowerCase();
    const existingEmails = new Set(
      receiversWithTSMAndUW.map((r) => r.email.toLowerCase())
    );
    
    if (submission?.tamEmail) {
      const tamEmailLower = submission.tamEmail.toLowerCase().trim();
      if (tamEmailLower && tamEmailLower !== senderEmailLower && !existingEmails.has(tamEmailLower)) {
        receiversWithTSMAndUW.push({
          email: submission.tamEmail.trim(),
          type: EmailRecipientType.CC,
        });
        existingEmails.add(tamEmailLower);
      }
    }
    
    if (submission?.uwEmail) {
      const uwEmailLower = submission.uwEmail.toLowerCase().trim();
      if (uwEmailLower && uwEmailLower !== senderEmailLower && !existingEmails.has(uwEmailLower)) {
        receiversWithTSMAndUW.push({
          email: submission.uwEmail.trim(),
          type: EmailRecipientType.CC,
        });
        existingEmails.add(uwEmailLower);
      }
    }

    if (submission?.correspondenceEmail) {
      const corrEmailLower = submission.correspondenceEmail.toLowerCase().trim();
      if (corrEmailLower && corrEmailLower !== senderEmailLower && !existingEmails.has(corrEmailLower)) {
        receiversWithTSMAndUW.push({
          email: submission.correspondenceEmail.trim(),
          type: EmailRecipientType.CC,
        });
        existingEmails.add(corrEmailLower);
      }
    }

    // Update headers to include TSM/UW in CC as fallback (if receivers array wasn't available)
    const updatedHeaders = (() => {
      // If we have receivers, headers will be ignored by useEmailForm, but update them anyway for consistency
      const headers = messageInfoWithReferences.headers ? { ...messageInfoWithReferences.headers } : {};
      const additionalCcEmails: string[] = [];
      
      if (submission?.tamEmail) {
        const tamEmailLower = submission.tamEmail.toLowerCase().trim();
        if (tamEmailLower && tamEmailLower !== senderEmailLower) {
          additionalCcEmails.push(submission.tamEmail.trim());
        }
      }
      
      if (submission?.uwEmail) {
        const uwEmailLower = submission.uwEmail.toLowerCase().trim();
        if (uwEmailLower && uwEmailLower !== senderEmailLower) {
          additionalCcEmails.push(submission.uwEmail.trim());
        }
      }

      if (submission?.correspondenceEmail) {
        const corrEmailLower = submission.correspondenceEmail.toLowerCase().trim();
        if (corrEmailLower && corrEmailLower !== senderEmailLower) {
          additionalCcEmails.push(submission.correspondenceEmail.trim());
        }
      }
      
      if (additionalCcEmails.length > 0) {
        const existingCc = headers?.Cc || headers?.cc || headers?.CC || '';
        const existingCcEmails = existingCc ? existingCc.split(',').map((e: string) => e.trim()).filter(Boolean) : [];
        const allCcEmails = [...existingCcEmails, ...additionalCcEmails].filter(
          (email, index, self) => 
            email && 
            email.toLowerCase() !== senderEmailLower &&
            self.findIndex((e) => e.toLowerCase() === email.toLowerCase()) === index
        );
        if (allCcEmails.length > 0) {
          headers.Cc = allCcEmails.join(', ');
        }
      }
      
      return headers;
    })();

    // Create a modified message that will result in AI-generated content being used
    const modifiedMessage: MessageInfo = {
      ...messageInfoWithReferences,
      // Include receivers with TSM/UW emails added (useEmailForm checks receivers first)
      receivers: receiversWithTSMAndUW.length > 0 ? receiversWithTSMAndUW : messageInfoWithReferences.receivers,
      // Update headers to include TSM/UW in CC (fallback if receivers array wasn't available)
      headers: updatedHeaders,
      aiGeneratedBody: generatedReply?.success ? generatedReply.bodyHtml : undefined,
      // Only include quote attachments that we uploaded via attachment-from-url API
      // Do NOT include original message attachments in replies
      attachments: quoteAttachments,
      // Keep the original message ID for proper references array construction
      // The draftMsgId will be handled separately in the send payload
      msgId: messageInfoWithReferences.msgId,
      // Preserve the original message ID for replyToMsgId
      replyToMsgId: messageInfoWithReferences.msgId,
    };

    return modifiedMessage;
  }, [messageInfoWithReferences, generatedReply, quoteAttachments, submission?.tamEmail, submission?.uwEmail, submission?.correspondenceEmail]);

  // Handle loading and error states
  const hasError =
    messageQuery.isError ||
    generateReplyQuery.isError ||
    (generatedReply && !generatedReply.success);
  const hasData = messageInfo && generatedReply?.success;

  if (hasError) {
    // Extract error message from API response, prioritizing errorMessage field
    const getErrorMessage = () => {
      // Check for API error response with errorMessage field
      const apiError = generateReplyQuery.error as
        | { response?: { data?: { errorMessage?: string } } }
        | undefined;
      if (apiError?.response?.data?.errorMessage) {
        return apiError.response.data.errorMessage;
      }

      // Check for messageQuery error
      if ((messageQuery.error as Error)?.message) {
        return (messageQuery.error as Error).message;
      }

      // Check for generateReplyQuery error message
      if ((generateReplyQuery.error as Error)?.message) {
        return (generateReplyQuery.error as Error).message;
      }

      // Check if generatedReply exists but success is false
      if (generatedReply && !generatedReply.success) {
        return 'Failed to generate reply';
      }

      return 'Unknown error';
    };

    const errorMessage = getErrorMessage();

    return (
      <Dialog
        open={true}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: '3px 3px 6px 0 rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            <ErrorOutlineIcon
              color="error"
              sx={{
                fontSize: '2rem',
                width: '2rem',
                height: '2rem',
              }}
            />
            <Typography
              variant="h6"
              component="h2"
              color="error"
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                textWrap: 'nowrap',
                width: '80%',
              }}
            >
              Unable to Generate Reply
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            pt: 2,
            pb: 2,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.5, mb: 2 }}
          >
            {errorMessage}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            MsgId: {msgId}, SubmissionId: {submissionId}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <CbButton
            buttonText="Close"
            styleName="cancel"
            onClick={handleClose}
            size="medium"
          />
        </DialogActions>
      </Dialog>
    );
  }

  if (!hasData || isProcessingAttachments) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
        }}
        onClick={handleClose}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <CircularProgress />
          <Typography>
            {messageQuery.isLoading
              ? 'Loading email data...'
              : generateReplyQuery.isLoading
              ? loadingMessages[currentLoadingMessageIndex]
              : isProcessingAttachments
              ? 'Creating draft and uploading quote attachments...'
              : 'Loading...'}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Only render CmailComposer when data is ready
  if (!hasData) {
    return null;
  }

  return (
    <CmailComposer
      open={true}
      onClose={handleClose}
      mode="reply"
      message={messageForComposer}
      msgCategory={messageInfo?.msgCategory}
      currentDraftMsgId={draftMsgId}
    />
  );
};

export const CmailComposerModalConfig = {
  CmailComposerModal: {
    component: CmailComposerModalComponent,
    config: {
      override: true,
    },
  },
};
